/**
 * La trousse de l'exercice « sur la feuille » (migration 0735, maquette `docs/brouillon/prototypes/
 * clavier-v3.html`) : elle remplace la barre d'icônes du haut et rassemble les outils de la feuille ET
 * ceux du player (Cours, Calculatrice, Boîte à outils, Défi, Me filmer, repères de l'énoncé).
 *
 * Elle est FIXE au bord droit, à mi-hauteur : la feuille garde toute la largeur de l'écran. Au repos
 * son zip bouge ; on la touche, elle se dézippe puis montre ce qu'elle contient ; on choisit, elle se
 * referme. Toucher ailleurs la referme sans rien sortir.
 */
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { serialLabel, trousseImage, useMyTrousse } from '@/lib/trousses';
import {
  sortTrousseItems,
  trousseAria,
  trousseCalls,
  type TrousseGroup,
  type TrousseItem,
} from './trousse-items';
import './trousse.css';

/** Chargée au premier toucher : la collection n'alourdit pas l'exercice. */
const TrousseCollectionModal = lazy(() =>
  import('@/features/trousse-collection/collection-modal').then((m) => ({
    default: m.TrousseCollectionModal,
  })),
);

/** La couverture de base, servie tant que la base n'a pas répondu. */
const DEFAULT_COVER = 'trousses/01.webp';
const UNZIP_MS = 330;
/** Sans activité, la trousse retourne se cacher (aux 3/4) au bord droit après ce délai. */
export const TROUSSE_TUCK_MS = 5000;

export function Trousse({
  items,
  onToggle,
  tuck = true,
}: {
  items: TrousseItem[];
  /** elle s'ouvre (le contenu est visible) ou se referme — la démo guidée s'en sert pour la faire découvrir */
  onToggle?: (open: boolean) => void;
  /**
   * Cachée aux 3/4 au bord droit (elle ne masque plus rien de la feuille, ex. « recopier ») : un premier
   * toucher la fait sortir, un second l'ouvre ; elle repart se cacher après 5 s sans activité. `false` :
   * toujours sortie (la démo guidée la fait découvrir d'un seul toucher).
   */
  tuck?: boolean;
}) {
  const { user } = useAuth();
  const { data } = useMyTrousse(user?.id);
  // hors d'un routeur (aperçus, tests) : le lien de la collection recharge la page
  const [open, setOpen] = useState(false);
  const [collectionOpen, setCollectionOpen] = useState(false);
  const [unzip, setUnzip] = useState(false);
  const [tucked, setTucked] = useState(tuck);
  // chaque geste relance le délai avant qu'elle reparte se cacher
  const [activity, setActivity] = useState(0);
  const touch = () => setActivity((n) => n + 1);
  const busy = useRef(false);
  // la dernière version du callback, pour que `close` reste stable (l'écouteur « toucher ailleurs » aussi)
  const notify = useRef(onToggle);
  notify.current = onToggle;
  const timers = useRef<number[]>([]);

  useEffect(
    () => () => {
      timers.current.forEach((t) => window.clearTimeout(t));
    },
    [],
  );

  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  const close = () => {
    setOpen(false);
    setUnzip(false);
    notify.current?.(false);
  };

  // sortie et refermée, sans geste pendant 5 s : elle retourne se cacher
  useEffect(() => {
    if (!tuck || tucked || open || unzip) return;
    const t = window.setTimeout(() => setTucked(true), TROUSSE_TUCK_MS);
    return () => window.clearTimeout(t);
  }, [tuck, tucked, open, unzip, activity]);

  const toggle = () => {
    touch();
    // cachée : le premier toucher la fait seulement sortir
    if (tuck && tucked) {
      setTucked(false);
      return;
    }
    if (busy.current) return;
    if (open) return close();
    busy.current = true;
    setUnzip(true);
    later(() => {
      busy.current = false;
      setOpen(true);
      notify.current?.(true);
    }, UNZIP_MS);
  };

  // la trousse ouverte : toucher ailleurs la referme (jamais un geste sur la feuille lui-même)
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest('.jr-tr-pop') || t?.closest('.jr-trousse')) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!items.length) return null;

  const trousse = data?.equipped ?? null;
  const name = trousse?.name ?? 'Graphite';
  const cover = trousseImage(trousse?.image_path ?? DEFAULT_COVER);
  const sorted = sortTrousseItems(items);
  const calls = trousseCalls(items) && !open;

  const pick = (it: TrousseItem) => {
    setOpen(false);
    notify.current?.(false);
    // elle se referme en sortant l'outil : le zip remonte après le geste
    setUnzip(true);
    later(() => setUnzip(false), 620);
    it.onPick();
  };

  return (
    <>
      <button
        type="button"
        className="jr-trousse"
        data-open={unzip ? '1' : '0'}
        data-tucked={tuck && tucked ? '1' : '0'}
        data-call={calls ? '1' : '0'}
        data-testid="trousse"
        aria-expanded={open}
        aria-label={trousseAria(name, items)}
        onClick={toggle}
      >
        <i className="jr-tr-cover" style={{ backgroundImage: `url(${cover})` }} aria-hidden />
        <span className="jr-tr-zip" aria-hidden="true">
          <i className="jr-tr-rail" />
          <i className="jr-tr-gap" />
          <i className="jr-tr-pull" />
        </span>
      </button>
      {open && (
        <div className="jr-tr-pop" role="menu" aria-label={`Ma trousse — ${name}`}>
          <p className="jr-tr-name">
            {name}
            {trousse && (
              <span className="jr-tr-serial">{serialLabel(trousse.serial, trousse.copies)}</span>
            )}
          </p>
          {user && !user.is_anonymous && (
            <a
              href="/trousses"
              role="menuitem"
              className="jr-tr-collection"
              onClick={(e) => {
                // une fenêtre par-dessus l'exercice : la refermer ramène où on était
                e.preventDefault();
                close();
                setCollectionOpen(true);
              }}
            >
              Ma collection de trousses
            </a>
          )}
          {sorted.map((it, i) => {
            const Icon = it.icon;
            const newGroup: TrousseGroup | null = i > 0 ? sorted[i - 1]!.group : null;
            return (
              <div key={it.id}>
                {newGroup && newGroup !== it.group && <hr />}
                <button
                  type="button"
                  role="menuitem"
                  data-call={it.highlight ? '1' : '0'}
                  data-active={it.active ? '1' : '0'}
                  onClick={() => pick(it)}
                >
                  {Icon ? (
                    <Icon aria-hidden />
                  ) : (
                    <span className="jr-tr-mark" aria-hidden>
                      {it.mark ?? '•'}
                    </span>
                  )}
                  <span>{it.label}</span>
                </button>
              </div>
            );
          })}
        </div>
      )}
      {collectionOpen && (
        <Suspense fallback={null}>
          <TrousseCollectionModal onClose={() => setCollectionOpen(false)} />
        </Suspense>
      )}
    </>
  );
}
